import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, FolderPlus, GitFork, Loader2, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { usePermissions } from '@/auth/usePermissions';
import { DeleteStatusBadge } from '@/features/delete/DeleteStatusBadge';
import { useDeleteFlow } from '@/features/delete/useDeleteFlow';
import {
  resolvedNameLabel,
  useResolveUserDisplayNames,
} from '@/features/patient-record/components/panels/CareTeamPanel/utils';
import { formatDate, formatDateTime } from '@/lib/format';

import {
  createFamilyForPedigree,
  createFamilylessPedigree,
  patchPedigreeName,
  personPedigreesQuery,
  type PersonPedigreeSummaryDto,
} from './personPedigrees';
import { PedigreeNameEditor } from './PedigreeNameEditor';

interface PedigreeSectionProps {
  /** The patient's person guid — the roster key the pedigree listing/creation is scoped to. */
  readonly guid: string;
}

/** Narrow an optional wire string to a present, non-empty value. */
function nonEmpty(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.length > 0;
}

/** Where the pedigree editor returns to on close: this patient record, Pedigree section selected. */
function returnToTarget(guid: string): string {
  return `/patients/${guid}#pedigree`;
}

/** Open a specific pedigree in the embedded editor, threading the return target for a clean exit. */
function openPedigreeUrl(guid: string, pedigreeGuid: string): string {
  const returnTo = encodeURIComponent(returnToTarget(guid));
  return `/patients/${guid}/pedigree?pedigree=${encodeURIComponent(pedigreeGuid)}&returnTo=${returnTo}`;
}

/**
 * Patient-record "Pedigree" content section (PTV2-1148). Lists the person's pedigrees as tiles —
 * including family-less (floating) ones the Family panel can't surface — and offers creation:
 *
 * - **New pedigree** (always): mints a family-less pedigree seeded with this patient as proband,
 *   then opens it.
 * - **New family** (only when the patient has no family yet): the existing family-create flow,
 *   which auto-mints the family's pedigree.
 *
 * Tiles are placeholders this iteration (a GitFork glyph + node count); the real canvas thumbnail
 * is deferred — see the TODO on {@link PedigreeTile}.
 */
export function PedigreeSection({ guid }: PedigreeSectionProps): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canCreateFamilies } = usePermissions();
  const query = useQuery(personPedigreesQuery(guid));

  const pedigrees = query.data ?? [];
  // A patient "has a family" when they are a member of any pedigree that belongs to one — that is
  // the only way a family pedigree surfaces here, so no separate families fetch is needed.
  const hasFamily = pedigrees.some((p) => nonEmpty(p.family_guid));

  const createMutation = useMutation({
    mutationFn: () => createFamilylessPedigree(guid),
    onSuccess: (pedigreeGuid) => {
      void queryClient.invalidateQueries({ queryKey: ['person', guid, 'pedigrees'] });
      void navigate(openPedigreeUrl(guid, pedigreeGuid));
    },
  });

  return (
    <Card id="pedigree" className="scroll-mt-4 rounded-xl">
      <CardHeader className="flex flex-row items-center justify-between gap-2 border-b border-border px-5 py-3.5 space-y-0">
        <CardTitle className="text-base font-semibold">
          {t('patient.pedigree.heading', { defaultValue: 'Family History' })}
        </CardTitle>
        <div className="flex items-center gap-2">
          {/* TODO(PTV2-1148 multi-family): the `!hasFamily` gate is a single-family limitation — a
              person can only belong to one family today, so "New family" is hidden once they have one.
              When the Family widget is reworked to support a person belonging to MULTIPLE families,
              drop the `!hasFamily` condition so the button ALWAYS shows (keep only `canCreateFamilies`). */}
          {!hasFamily && canCreateFamilies && (
            <Button asChild size="sm" variant="ghost" className="gap-1.5 text-primary">
              <Link to={`/families/new?probandPersonId=${encodeURIComponent(guid)}`}>
                <FolderPlus className="h-3.5 w-3.5" aria-hidden="true" />
                {t('patient.pedigree.newFamily', { defaultValue: 'New family' })}
              </Link>
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            loading={createMutation.isPending}
            onClick={() => {
              createMutation.mutate();
            }}
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            {t('patient.pedigree.newPedigree', { defaultValue: 'New pedigree' })}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-5">
        {query.isPending ? (
          <p className="text-sm text-muted-foreground">
            {t('patient.pedigree.loading', { defaultValue: 'Loading pedigrees…' })}
          </p>
        ) : query.isError ? (
          <div className="flex flex-col items-start gap-2">
            <p className="text-sm text-destructive">
              {t('patient.pedigree.error', {
                defaultValue: 'The pedigrees for this patient could not be loaded.',
              })}
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                void query.refetch();
              }}
            >
              {t('patient.pedigree.retry', { defaultValue: 'Retry' })}
            </Button>
          </div>
        ) : pedigrees.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">
            {t('patient.pedigree.empty', {
              defaultValue: 'This patient is not part of any pedigree yet.',
            })}
          </p>
        ) : (
          <ul className="flex flex-wrap gap-4">
            {pedigrees.map((pedigree) => (
              <li key={pedigree.pedigree_guid}>
                <PedigreeTile
                  pedigree={pedigree}
                  personGuid={guid}
                  onOpen={() => {
                    void navigate(openPedigreeUrl(guid, pedigree.pedigree_guid));
                  }}
                />
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

interface PedigreeTileProps {
  readonly pedigree: PersonPedigreeSummaryDto;
  /** The patient's person guid — the roster key whose pedigree list is re-invalidated after a delete. */
  readonly personGuid: string;
  readonly onOpen: () => void;
}

/**
 * A ~210×300 pedigree tile: a compact GitFork thumbnail (membership count) over a detail footer.
 *
 * The footer carries the pedigree's metadata as aligned label rows — Family (a link to the family
 * record, or "No family"), Proband, and Source (the recorded source label, falling back to the
 * creator's resolved display name) — plus a "last updated" line (timestamp + last-modifier name).
 * Creator / modifier guids are resolved to display names via the shared user-directory join.
 *
 * A `Trash2` affordance (bottom-right of the footer) runs the full delete flow (preview →
 * confirm/wizard → job) for this pedigree (scenario d). While the delete job runs the tile is dimmed
 * and shows a "Deleting…" badge; on terminal success the person's pedigree list is invalidated so the
 * tile disappears.
 *
 * TODO(PTV2-1148 follow-up): replace the glyph placeholder with a real rendered pedigree thumbnail
 * (a static preview of the canvas) once a preview/export endpoint exists.
 */
function PedigreeTile({ pedigree, personGuid, onOpen }: PedigreeTileProps): ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const openLabel = t('patient.pedigree.open', { defaultValue: 'Open pedigree' });

  const label =
    nonEmpty(pedigree.family_name)
      ? pedigree.family_name
      : nonEmpty(pedigree.proband_name)
        ? pedigree.proband_name
        : t('patient.pedigree.thisPedigree', { defaultValue: 'this pedigree' });

  // Resolve creator + last-modifier guids to display names in one batched, cached directory join.
  const resolvedNames = useResolveUserDisplayNames(
    [pedigree.author, pedigree.modified_by].filter(nonEmpty),
  );
  // Lookup in flight → null (left out); lookup failed → "Unknown user".
  const unknownUser = t('patient.careTeam.unknownUser');
  const authorName = nonEmpty(pedigree.author)
    ? resolvedNameLabel(resolvedNames.get(pedigree.author), unknownUser)
    : null;
  const modifiedByName = nonEmpty(pedigree.modified_by)
    ? resolvedNameLabel(resolvedNames.get(pedigree.modified_by), unknownUser)
    : null;
  // "Source:" prefers the recorded source label; when blank it falls back to the creator's name.
  const sourceValue = nonEmpty(pedigree.source_label) ? pedigree.source_label : authorName;

  const onDeleted = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['person', personGuid, 'pedigrees'] });
  }, [queryClient, personGuid]);

  // Whether this pedigree already belongs to a family — drives the blue tile contour and gates the
  // per-tile "Create family" affordance (floating tiles only).
  const belongsToFamily = nonEmpty(pedigree.family_guid);
  // A family can only be created when the proband has a last name (the family name is derived from it).
  const canCreateFamily = nonEmpty(pedigree.proband_last_name);

  const createFamilyMutation = useMutation({
    mutationFn: () => {
      if (!nonEmpty(pedigree.proband_last_name)) {
        throw new Error('Proband last name is required to create a family.');
      }
      return createFamilyForPedigree({
        pedigreeGuid: pedigree.pedigree_guid,
        name: `${pedigree.proband_last_name} Family`,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['person', personGuid, 'pedigrees'] });
    },
    onError: (error: unknown) => {
      console.error('Failed to create family for pedigree', error);
    },
  });

  const del = useDeleteFlow({
    targetType: 'PEDIGREE',
    guid: pedigree.pedigree_guid,
    entityLabel: label,
    onDeleted,
  });

  const deleteLabel = t('patient.pedigree.delete', { defaultValue: 'Delete pedigree' });
  const emptyValue = '—';

  return (
    <div
      aria-busy={del.isBusy}
      className={`relative flex h-[268px] w-[210px] flex-col overflow-hidden rounded-xl border border-border bg-card${
        del.isBusy ? ' pointer-events-none opacity-60' : ''
      }`}
    >
      {/* Family pedigrees get a corner label (same colour as the family-name link) instead of a contour. */}
      {belongsToFamily && (
        <span className="pointer-events-none absolute left-0 top-0 z-10 rounded-br-md bg-primary px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white">
          {t('patient.pedigree.familyBadge', { defaultValue: 'Family' })}
        </span>
      )}
      {/* The preview itself is the opener — clicking anywhere on the thumbnail opens the pedigree.
          Placeholder preview for now — see TODO on PedigreeTile for the real thumbnail. */}
      <button
        type="button"
        onClick={onOpen}
        title={openLabel}
        aria-label={openLabel}
        className="flex h-[104px] shrink-0 cursor-pointer flex-col items-center justify-center gap-1.5 bg-muted/40 text-muted-foreground transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
      >
        <GitFork className="h-8 w-8" aria-hidden="true" />
        <span className="px-2 text-center text-xs tabular-nums">
          {t('patient.pedigree.nodeCount', {
            defaultValue: '{{count}} people',
            count: pedigree.node_count,
          })}
          {pedigree.linked_patient_count > 1 && (
            <>
              {' · '}
              {t('patient.pedigree.sharedWith', {
                defaultValue: 'shared with {{count}} patients',
                count: pedigree.linked_patient_count,
              })}
            </>
          )}
        </span>
      </button>
      <div className="flex min-w-0 flex-1 flex-col gap-1 border-t border-border p-2.5 text-xs">
        {/* Pedigree name — floating pedigrees only (family pedigrees are named by their family). */}
        {!belongsToFamily && (
          <TilePedigreeNameField
            pedigreeGuid={pedigree.pedigree_guid}
            personGuid={personGuid}
            initialName={pedigree.pedigree_name ?? ''}
          />
        )}
        {/* Family — aligned label row; the value links to the family record when there is one. */}
        <div className="flex min-w-0 items-baseline gap-1.5">
          <span className="shrink-0 text-muted-foreground">
            {t('patient.pedigree.familyLabel', { defaultValue: 'Family:' })}
          </span>
          {belongsToFamily ? (
            <Link
              to={`/families/${pedigree.family_guid}`}
              className="inline-flex min-w-0 items-center gap-1 font-medium text-primary no-underline hover:underline"
            >
              <span className="truncate">
                {nonEmpty(pedigree.family_name)
                  ? pedigree.family_name
                  : t('patient.pedigree.openFamilyRecord', { defaultValue: 'Open family record' })}
              </span>
              <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
            </Link>
          ) : (
            <CreateFamilyButton
              disabled={!canCreateFamily}
              pending={createFamilyMutation.isPending}
              onCreate={() => {
                createFamilyMutation.mutate();
              }}
            />
          )}
        </div>
        {/* Proband — aligned label row. */}
        <div className="flex min-w-0 items-baseline gap-1.5">
          <span className="shrink-0 text-muted-foreground">
            {t('patient.pedigree.probandLabel', { defaultValue: 'Proband:' })}
          </span>
          <span className="truncate font-medium text-foreground">
            {nonEmpty(pedigree.proband_name) ? pedigree.proband_name : emptyValue}
          </span>
        </div>
        {/* Source — the recorded label, falling back to the creator's resolved display name. When the
            pedigree was materialized from a questionnaire, the label links to the patient's
            questionnaires (where the originating request lives). */}
        <div className="flex min-w-0 items-baseline gap-1.5">
          <span className="shrink-0 text-muted-foreground">
            {t('patient.pedigree.sourceLabel', { defaultValue: 'Source:' })}
          </span>
          {pedigree.source_type === 'QUESTIONNAIRE' &&
          nonEmpty(pedigree.source_questionnaire_request_guid) &&
          nonEmpty(sourceValue) ? (
            <Link
              to={`/patients/${encodeURIComponent(personGuid)}#questionnaires`}
              className="truncate font-medium text-primary no-underline hover:underline"
              title={sourceValue}
            >
              {sourceValue}
            </Link>
          ) : (
            <span className="truncate text-foreground">{nonEmpty(sourceValue) ? sourceValue : emptyValue}</span>
          )}
        </div>
        {/* Last-updated line + delete affordance, pinned to the footer bottom. */}
        <div className="mt-auto flex items-end justify-between gap-2 pt-1">
          <div className="min-w-0 text-[11px] leading-tight text-muted-foreground">
            <p className="truncate" title={formatDateTime(pedigree.modified_on)}>
              {t('patient.pedigree.lastUpdated', {
                defaultValue: 'Last updated {{when}}',
                when: formatDate(pedigree.modified_on),
              })}
            </p>
            {nonEmpty(modifiedByName) && (
              <p className="truncate">
                {t('patient.pedigree.lastUpdatedBy', {
                  defaultValue: 'by {{name}}',
                  name: modifiedByName,
                })}
              </p>
            )}
          </div>
          {del.phase !== null ? (
            <DeleteStatusBadge phase={del.phase} />
          ) : (
            <button
              type="button"
              onClick={del.start}
              title={deleteLabel}
              aria-label={deleteLabel}
              className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
      {del.overlay}
    </div>
  );
}

interface TilePedigreeNameFieldProps {
  readonly pedigreeGuid: string;
  readonly personGuid: string;
  readonly initialName: string;
}

/**
 * Inline "Pedigree name" editor on a FLOATING pedigree's tile (PTV2-1157). Saves via the granular
 * pedigree PATCH on blur / Enter; family pedigrees are named by their family, so this is shown only
 * for pedigrees without one.
 */
function TilePedigreeNameField({ pedigreeGuid, personGuid, initialName }: TilePedigreeNameFieldProps): ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [value, setValue] = useState(initialName);
  // Resync if the tile refetches a new saved value (e.g. after another edit).
  useEffect(() => {
    setValue(initialName);
  }, [initialName]);

  const save = useMutation({
    mutationFn: (name: string) => patchPedigreeName(pedigreeGuid, name),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['person', personGuid, 'pedigrees'] });
    },
  });

  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <span className="shrink-0 text-muted-foreground">
        {t('patient.pedigree.nameLabel', { defaultValue: 'Name:' })}
      </span>
      <PedigreeNameEditor
        value={value}
        saving={save.isPending}
        onSave={(next) => {
          setValue(next);
          save.mutate(next);
        }}
        placeholder={t('routes.pedigree.legend.pedigreeNamePlaceholder', {
          defaultValue: 'Name this pedigree…',
        })}
        emptyLabel={t('routes.pedigree.legend.pedigreeNamePlaceholder', {
          defaultValue: 'Name this pedigree…',
        })}
        className="min-w-0 flex-1"
        valueClassName="flex-1"
      />
    </div>
  );
}

interface CreateFamilyButtonProps {
  /** Disabled because the proband has no last name — hovering shows the reason. */
  readonly disabled: boolean;
  /** The family-create request is in flight — shows a spinner and blocks re-clicks. */
  readonly pending: boolean;
  readonly onCreate: () => void;
}

/**
 * Per-tile "Create family" affordance for a floating pedigree (PTV2-1148). Disabled (with an
 * explanatory tooltip) until the proband has a last name — the derived family name needs it.
 */
function CreateFamilyButton({ disabled, pending, onCreate }: CreateFamilyButtonProps): ReactElement {
  const { t } = useTranslation();
  const label = t('patient.pedigree.createFamily', { defaultValue: 'Create family' });

  const button = (
    <Button
      type="button"
      size="sm"
      variant="outline"
      disabled={disabled || pending}
      onClick={onCreate}
      className="h-6 min-w-0 gap-1 px-2 text-xs"
    >
      {pending ? (
        <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden="true" />
      ) : (
        <FolderPlus className="h-3 w-3 shrink-0" aria-hidden="true" />
      )}
      <span className="truncate">{label}</span>
    </Button>
  );

  if (!disabled) return button;

  // A disabled button emits no pointer events, so anchor the tooltip to a focusable wrapper span —
  // that surfaces the reason the action is unavailable on hover / focus.
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* Focusable wrapper so keyboard users can surface the "why disabled" reason (a disabled
              button is itself not focusable). */}
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- intentional: makes the disabled-reason discoverable on focus */}
          <span tabIndex={0} className="inline-flex min-w-0">
            {button}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {t('routes.pedigree.tile.createFamilyNeedsLastName', {
            defaultValue: 'Proband must have a last name',
          })}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
